import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Tab, Tabs, Table, Form, Row, Col, Button, Badge, Modal, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney, fmtDate } from '../lib/format.js';

const TYPE_LABEL = {
  asset: 'Aktiv',  liability: 'Passiv', equity: 'Eigenkap.',
  income: 'Erlös', expense: 'Aufwand',
  vat_in: 'Vorsteuer', vat_out: 'USt',
  bank: 'Bank', cash: 'Kasse',
  receivable: 'Forderungen', payable: 'Verbindlichk.'
};

export default function AccountingPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canManage = auth.can('accounting', 'manage');
  const today = new Date();
  const yearStart = `${today.getFullYear()}-01-01`;
  const yearEnd   = `${today.getFullYear()}-12-31`;
  const [from, setFrom] = useState(yearStart);
  const [to,   setTo]   = useState(yearEnd);

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get('/accounting/accounts').then(r => r.data.items)
  });
  const bookings = useQuery({
    queryKey: ['bookings', from, to],
    queryFn: () => api.get('/accounting/bookings', { params: { from, to, limit: 1000 } }).then(r => r.data.items)
  });

  // ---- Account create modal ----
  const [acctModal, setAcctModal] = useState(false);
  const [acctDraft, setAcctDraft] = useState({ framework: 'custom', number: '', name: '', type: 'expense', vat_rate: '' });
  const [acctError, setAcctError] = useState(null);
  const openAcct = () => { setAcctDraft({ framework: 'custom', number: '', name: '', type: 'expense', vat_rate: '' }); setAcctError(null); setAcctModal(true); };
  const createAccount = useMutation({
    mutationFn: () => api.post('/accounting/accounts', {
      framework: acctDraft.framework,
      number: acctDraft.number,
      name: acctDraft.name,
      type: acctDraft.type,
      vat_rate: acctDraft.vat_rate === '' ? null : parseFloat(acctDraft.vat_rate)
    }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['accounts'] }); setAcctModal(false); },
    onError: (e) => setAcctError(e.response?.data?.message || e.message)
  });

  const exportCsv = async () => {
    const r = await api.get('/accounting/export.csv', { params: { from, to }, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `buchungen-${from}_${to}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // Group accounts by type for the chart of accounts tab
  const accountGroups = (accounts.data || []).reduce((m, a) => {
    (m[a.type] ||= []).push(a); return m;
  }, {});

  return (
    <>
      <PageHeader title="Buchhaltung" subtitle="Konten-Plan und Buchungssätze (SKR03)"
        actions={
          <>
            {canManage && (
              <Button variant="outline-secondary" onClick={openAcct}>
                <Icon icon="tabler:plus" className="me-1" />Konto anlegen
              </Button>
            )}
            {auth.can('accounting', 'export') && (
              <Button variant="outline-secondary" onClick={exportCsv}>
                <Icon icon="tabler:download" className="me-1" />DATEV-CSV
              </Button>
            )}
          </>
        } />

      <Tabs defaultActiveKey="bookings" className="mb-3">
        <Tab eventKey="bookings" title={<>Buchungen <Badge bg="" className="badge-soft-secondary">{bookings.data?.length ?? 0}</Badge></>}>
          <Card className="card-flat mb-3">
            <Card.Body className="py-3">
              <Row className="g-2 align-items-end">
                <Col sm={4}><Form.Group>
                  <Form.Label className="small text-muted mb-1">Von</Form.Label>
                  <Form.Control size="sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </Form.Group></Col>
                <Col sm={4}><Form.Group>
                  <Form.Label className="small text-muted mb-1">Bis</Form.Label>
                  <Form.Control size="sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </Form.Group></Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="card-flat">
            <Card.Body className="p-0">
              {bookings.isLoading && <div className="p-4 text-muted">Lade…</div>}
              {bookings.data && bookings.data.length === 0 && (
                <div className="empty-state">
                  <Icon icon="tabler:book-off" className="empty-icon" />
                  <div>Keine Buchungen im Zeitraum.</div>
                </div>
              )}
              {bookings.data && bookings.data.length > 0 && (
                <Table hover className="mb-0 align-middle small">
                  <thead><tr>
                    <th>Datum</th><th>Beleg</th><th>Buchungstext</th>
                    <th>Soll</th><th>Haben</th><th className="text-end">Betrag</th>
                  </tr></thead>
                  <tbody>
                    {bookings.data.map(b => {
                      const debit = b.lines.find(l => l.debit_cents > 0);
                      const credit = b.lines.find(l => l.credit_cents > 0);
                      const amount = Math.max(debit?.debit_cents || 0, credit?.credit_cents || 0);
                      return (
                        <tr key={b.id}>
                          <td className="text-nowrap">{fmtDate(b.booking_date)}</td>
                          <td>
                            <Badge bg="" className="badge-soft-secondary me-1">{b.doc_type}</Badge>
                            {b.doc_number && <code className="small">{b.doc_number}</code>}
                          </td>
                          <td>{b.narrative}</td>
                          <td>{debit ? <span><strong>{debit.account_number}</strong> {debit.account_name.slice(0, 30)}</span> : '—'}</td>
                          <td>{credit ? <span><strong>{credit.account_number}</strong> {credit.account_name.slice(0, 30)}</span> : '—'}</td>
                          <td className="text-end">{fmtMoney(amount, b.currency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="accounts" title={<>Konten <Badge bg="" className="badge-soft-secondary">{accounts.data?.length ?? 0}</Badge></>}>
          <Row className="g-3">
            {Object.entries(accountGroups).map(([type, accs]) => (
              <Col md={6} key={type}>
                <Card className="card-flat h-100">
                  <Card.Body>
                    <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
                      {TYPE_LABEL[type] || type}
                    </h6>
                    <Table size="sm" className="mb-0 align-middle">
                      <tbody>
                        {accs.map(a => (
                          <tr key={a.id}>
                            <td className="text-nowrap"><code>{a.number}</code></td>
                            <td>{a.name}</td>
                            <td className="text-end text-muted">{a.vat_rate ? `${a.vat_rate}%` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Tab>
      </Tabs>

      <Modal show={acctModal} onHide={() => setAcctModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Neues Konto</Modal.Title></Modal.Header>
        <Modal.Body>
          {acctError && <Alert variant="danger" className="py-2 small">{acctError}</Alert>}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Konten-Nummer *</Form.Label>
                <Form.Control required value={acctDraft.number}
                  onChange={(e) => setAcctDraft({ ...acctDraft, number: e.target.value })}
                  placeholder="z.B. 8410" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Framework</Form.Label>
                <Form.Select value={acctDraft.framework} onChange={(e) => setAcctDraft({ ...acctDraft, framework: e.target.value })}>
                  <option value="custom">Eigen</option>
                  <option value="skr03">SKR03</option>
                  <option value="skr04">SKR04</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Bezeichnung *</Form.Label>
                <Form.Control required value={acctDraft.name}
                  onChange={(e) => setAcctDraft({ ...acctDraft, name: e.target.value })}
                  placeholder="z.B. Erlöse 19% USt aus Lizenzen" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Typ *</Form.Label>
                <Form.Select value={acctDraft.type} onChange={(e) => setAcctDraft({ ...acctDraft, type: e.target.value })}>
                  <option value="income">Erlös</option>
                  <option value="expense">Aufwand</option>
                  <option value="asset">Aktiv</option>
                  <option value="liability">Passiv</option>
                  <option value="equity">Eigenkapital</option>
                  <option value="bank">Bank</option>
                  <option value="cash">Kasse</option>
                  <option value="receivable">Forderungen</option>
                  <option value="payable">Verbindlichkeiten</option>
                  <option value="vat_in">Vorsteuer</option>
                  <option value="vat_out">Umsatzsteuer</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>MwSt.-Satz (%)</Form.Label>
                <Form.Control type="number" step="0.5" value={acctDraft.vat_rate}
                  onChange={(e) => setAcctDraft({ ...acctDraft, vat_rate: e.target.value })}
                  placeholder="leer = nicht relevant" />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setAcctModal(false)}>Abbrechen</Button>
          <Button onClick={() => createAccount.mutate()}
            disabled={!acctDraft.number || !acctDraft.name || createAccount.isLoading}>
            Anlegen
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
